export type RegistrationPlan = "fast" | "ppv";

export interface RegistrationRequest {
  plan: RegistrationPlan;
  deviceId: string;
  name: string;
  phone: string;
  city: string;
  // PPV only
  address?: string;
  postalCode?: string;
  nif?: string;
  // The customer ticked the consent box on the form
  consent: boolean;
}

// EvoMedia Device Update API: updates the customer details of a device that
// already exists in the Innplay database (it does not create devices).
const UPDATE_DEVICE_URL = "https://integrations-api.innplay.cloud/evomedia/update-device";

// SECURITY: the API docs say this key must stay confidential and not ship in
// client apps, and anything in this bundle can be extracted from the APK.
// Move this call behind our own backend, which would hold the key and
// forward the request, before releasing.
const INTEGRATION_KEY = "63eb80330c38a2049e28092026";

const SERVICE_TYPE: Record<RegistrationPlan, string> = {
  fast: "FAST_TV",
  ppv: "PPV_TV",
};

/**
 * Sends a registration typed on the TV with the remote to the EvoMedia
 * Device Update API. Throws an Error (shown on the form) when it fails.
 */
export async function submitRegistration(req: RegistrationRequest): Promise<void> {
  const body: Record<string, unknown> = {
    service_type: SERVICE_TYPE[req.plan],
    deviceid: req.deviceId,
    name: req.name,
    phone: req.phone,
    city: req.city,
  };
  if (req.plan === "ppv") {
    body.address = req.address;
    body.postal_code = req.postalCode;
    body.nif = req.nif;
    body.requires_activation = true;
  }
  body.consent = req.consent;
  // UTC ISO 8601 with milliseconds and "Z", as the API requires
  body.submitted_at = new Date().toISOString();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);
  let response: Response;
  try {
    response = await fetch(UPDATE_DEVICE_URL, {
      method: "POST",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        "X-Integration-Key": INTEGRATION_KEY,
      },
      body: JSON.stringify(body),
    });
  } catch (e: any) {
    throw new Error(
      e?.name === "AbortError"
        ? "The server took too long to answer. Please try again."
        : "Could not reach the server. Check your connection and try again."
    );
  } finally {
    clearTimeout(timeoutId);
  }

  // Status only, not the body: it echoes the customer's personal data
  console.log(`📝 update-device (${body.service_type}) → HTTP ${response.status}`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const message = data?.message || data?.error || data?.detail;
    throw new Error(
      typeof message === "string" && message
        ? message
        : `Registration failed (error ${response.status}). Please try again.`
    );
  }
}
